"use client";

import { useEffect } from "react";
import { usePathname, useRouter } from "next/navigation";
import { FormattedMessage, useIntl } from "react-intl";
import { FaArrowRight, FaGift } from "react-icons/fa6";
import { MdClose } from "react-icons/md";

import { useAppDispatch, useAppSelector } from "@/app/store/hooks";
import {
  dismiss,
  hydrate,
  PROMO_DISMISSED_ATTR,
} from "@/app/store/promoBannerSlice";
import { trackEvent } from "@/app/providers/ObservabilityProvider";
import { useLocale } from "@/app/providers/IntlProvider";
import { navigateToSection } from "./profileNav";

/**
 * Full-width promo strip above the navbar, pinned from `lg` and scrolling away below it.
 * It sits outside the document flow, so its height is published as `--promo-h`: the
 * page wrapper pads by it and both navbars offset their `top` by it.
 */
const PromoBanner = () => {
  const intl = useIntl();
  const router = useRouter();
  const currentRoute = usePathname();
  const { locale } = useLocale();
  const dispatch = useAppDispatch();
  const dismissed = useAppSelector((state) => state.promoBanner.dismissed);

  useEffect(() => {
    dispatch(hydrate());
  }, [dispatch]);

  // Collapse the space reserved in globals.css once the banner is gone. On a reload the
  // pre-paint script in app/layout.tsx has already set the attribute, so the banner and
  // its space never show; this covers a dismissal made on the page.
  useEffect(() => {
    if (dismissed) document.documentElement.setAttribute(PROMO_DISMISSED_ATTR, "");
  }, [dismissed]);

  if (dismissed) return null;

  return (
    // Pinned from lg; on phones and tablets it scrolls away with the page (the app bar then
    // takes the top of the screen alone, see MobileNavbar), so it never costs a permanent strip.
    <div className="promo-banner absolute inset-x-0 top-0 z-40 h-12 lg:fixed bg-blue-main text-white [--focus-ring:white] flex items-center justify-center pl-3 pr-12 sm:px-14">
      <div className="flex items-center gap-2 min-w-0 text-xs sm:text-sm font-medium">
        <FaGift aria-hidden="true" className="shrink-0" />
        <span className="truncate">
          <span className="hidden sm:inline">
            <FormattedMessage id="promo.banner.text" />
          </span>
          <span className="sm:hidden">
            <FormattedMessage id="promo.banner.textShort" />
          </span>
        </span>
        <span aria-hidden="true" className="hidden sm:inline text-white/50">
          ·
        </span>
        {/* py-3 -my-3: a 40 px tall hit area inside the 48 px strip, same look. */}
        <button
          type="button"
          onClick={() => {
            trackEvent("promo-banner-cta");
            navigateToSection(currentRoute, router, "Individual", "pricing", locale);
          }}
          className="-my-3 flex items-center gap-1.5 shrink-0 py-3 font-semibold underline underline-offset-4 hover:decoration-2 transition-all cursor-pointer"
        >
          <FormattedMessage id="promo.banner.cta" />
          <FaArrowRight aria-hidden="true" className="text-[0.7em]" />
        </button>
      </div>

      {/* 44 px hit area around the 28 px visible disc (touch target, WCAG 2.5.8). */}
      <button
        type="button"
        aria-label={intl.formatMessage({ id: "a11y.promoClose" })}
        onClick={() => {
          trackEvent("promo-banner-dismiss");
          dispatch(dismiss());
        }}
        className="group/close absolute right-0.5 sm:right-3 w-11 h-11 flex items-center justify-center rounded-full cursor-pointer"
      >
        <span className="flex w-7 h-7 items-center justify-center rounded-full bg-white/15 transition-colors group-hover/close:bg-white/25">
          <MdClose aria-hidden="true" className="text-base" />
        </span>
      </button>
    </div>
  );
};

export default PromoBanner;
